import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import DottedArrowIcon from '../../components/DottedArrowIcon';
import LatestComment from '../../components/Contracts/LatestComment';
import api from '../../utils/api';
import Footer from '../../components/Footer/Footer';
import DataTablePagination from '../../components/Table/DataTablePagination';
import SearchField from '../../components/Table/SearchField';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useListPage } from '../../hooks/useListPage';
import { toast } from 'react-toastify';
import StatusLabel from '../../components/Status/StatusLabel';
import { useAuth } from '../../context/AuthContext';
import { CONTRACT_STATUS_OPTIONS, canOpenContract, contractStatusLabel, displayContractStatus } from '../../utils/contractStatus';

const bounceRight = keyframes`
  0%, 100% { transform: translateX(0px); }
  50% { transform: translateX(5px); }
`;

const ContractList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [activeTab, setActiveTab] = useState('All');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [statusFilter, setStatusFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  // Any change to what is being listed restarts at the first page — otherwise
  // a narrower result set leaves you on a page that no longer exists.
  const [page, setPage] = useListPage(JSON.stringify([debouncedSearch, rowsPerPage, activeTab, statusFilter, sortOrder]));

  // Zeros until the counts arrive, not placeholder numbers that read as real.
  const [stats, setStats] = useState({
    active: 0,
    expiringSoon: 0,
    expired: 0,
    prequalified: null,
  });

  useEffect(() => {
    fetchContracts();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, activeTab, statusFilter, sortOrder]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/contracts/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: rowsPerPage,
        search: debouncedSearch,
        sortOrder,
      };

      if (activeTab === 'Registered Suppliers') params.supplierType = 'registered';
      if (statusFilter) params.status = statusFilter;

      const response = await api.get('/contracts', { params });

      if (response.data.success) {
        setContracts(response.data.data || []);
        setPagination(response.data.pagination || { total: 0, pages: 1 });
      } else {
        setContracts([]);
        setPagination({ total: 0, pages: 1 });
      }
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
      toast.error('Failed to fetch contracts');
      setContracts([]);
      setPagination({ total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const getApplicationUpdatedAt = (contract) => {
    return contract.applicationUpdatedAt ||
      contract.supplier?.updatedAt ||
      contract.supplier?.submittedAt ||
      contract.updatedAt ||
      contract.createdAt;
  };

  const getContractStatusChip = (contract) => {
    const status = displayContractStatus(contract);
    // The design has slightly rounded rect chips here.
    return <StatusLabel kind="contract" status={status} label={contractStatusLabel(status)} sx={{ borderRadius: '4px' }} />;
  };

  const tabs = ['All', 'Registered Suppliers'];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', pb: 0 }}>
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 3, sm: 5 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
          flex: 1,
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              mb: 0.5,
              color: '#111827',
              fontSize: { xs: '22px', sm: '28px' },
              letterSpacing: '-0.01em',
            }}
          >
            Supplier Contract Management
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: { xs: '13px', sm: '14px' },
              lineHeight: 1.6,
              mb: 3
            }}
          >
            Manage your onboarded supplier contracts effortlessly.
          </Typography>

          {/* Tabs */}
          <Box sx={{ display: 'flex', gap: 1, p: 0.5, bgcolor: '#f9fafb', borderRadius: '8px', width: 'fit-content' }}>
            {tabs.map((tab) => (
              <Button
                key={tab}
                onClick={() => setActiveTab(tab)}
                sx={{
                  textTransform: 'none',
                  color: activeTab === tab ? '#111827' : '#6b7280',
                  fontWeight: activeTab === tab ? 600 : 500,
                  fontSize: '14px',
                  bgcolor: activeTab === tab ? '#fff' : 'transparent',
                  px: 2,
                  py: 0.75,
                  borderRadius: '6px',
                  boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  '&:hover': {
                    bgcolor: activeTab === tab ? '#fff' : '#f3f4f6',
                  }
                }}
              >
                {tab}
              </Button>
            ))}
          </Box>
        </Box>

        {/* 4 Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                  Active Contracts
                </Typography>
                <DescriptionIcon sx={{ color: '#4d8c2e', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '28px', color: '#111827' }}>
                {stats.active}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                  Expiring Soon Contracts
                </Typography>
                <DescriptionIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '28px', color: '#111827' }}>
                {stats.expiringSoon}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                  Expired Contracts
                </Typography>
                <DescriptionIcon sx={{ color: '#dc2626', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '28px', color: '#111827' }}>
                {stats.expired}
              </Typography>
            </Paper>
          </Grid>
          {/* Staff only: the server sends no prequalified counts to a supplier. */}
          {stats.prequalified && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                onClick={() => setStatusFilter('pending_upload')}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  '&:hover': { borderColor: '#9ca3af' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                    Prequalified and Pending Contract Upload
                  </Typography>
                  <DescriptionIcon sx={{ color: '#d97706', fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '28px', color: '#111827' }}>
                  {stats.prequalified.total}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '12px', mt: 0.5 }}>
                  {stats.prequalified.applicable} applicable · {stats.prequalified.undecided} not yet decided
                  {' · '}{stats.prequalified.notApplicable} completed without a contract
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Supplier List Section Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              color: '#111827',
              fontSize: '18px',
            }}
          >
            Supplier List
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: '14px',
            }}
          >
            View and manage your onboarded suppliers here
          </Typography>
        </Box>

        {/* Search, Filter and Download */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search contract no., title, supplier"
            sx={{
              minWidth: { xs: '100%', sm: '180px' },
              maxWidth: { xs: '100%', sm: '300px' },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              sx={{ borderRadius: '6px', fontSize: '14px', backgroundColor: '#fff' }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {CONTRACT_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={sortOrder === 'asc' ? 'Oldest first — click for newest first' : 'Newest first — click for oldest first'}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSortOrder(s => s === 'asc' ? 'desc' : 'asc')}
              startIcon={sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              sx={{
                borderColor: '#e5e7eb',
                color: '#374151',
                textTransform: 'none',
                fontSize: '13px',
                px: 2,
                py: 0.75,
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' }
              }}
            >
              {sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
            </Button>
          </Tooltip>
        </Box>

        {/* Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: '#fff',
            overflow: 'hidden'
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fff' }}>
                <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', py: 2, borderBottom: '1px solid #e5e7eb' }}>Vendor Number</TableCell>
                <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', py: 2, borderBottom: '1px solid #e5e7eb' }}>Company Name</TableCell>
                <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', py: 2, borderBottom: '1px solid #e5e7eb' }}>Supplier Type</TableCell>
                <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', py: 2, borderBottom: '1px solid #e5e7eb' }}>Last Updated</TableCell>
                <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', py: 2, borderBottom: '1px solid #e5e7eb' }}>Contract Status</TableCell>
                <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', py: 2, borderBottom: '1px solid #e5e7eb' }}>Latest Comment</TableCell>
                <TableCell align="right" sx={{ py: 2, borderBottom: '1px solid #e5e7eb', width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, borderBottom: 0 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : contracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 0 }}>
                    <Typography sx={{ color: '#6b7280', fontSize: '14px' }}>
                      No contracts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contracts.map((contract) => {
                  const vendorNumber = contract.supplier?.vendorNumber || contract.vendorNumber || '-';
                  const supplierName = contract.supplier?.supplierName || contract.supplierName || '-';
                  const canOpen = canOpenContract(user);

                  return (
                    <TableRow
                      key={contract._id}
                      hover
                      sx={{
                        cursor: canOpen ? 'pointer' : 'default',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                      onClick={() => {
                        if (!canOpen) return;
                        if (contract.isPlaceholder && contract.supplier?._id) {
                          navigate('/contracts');
                        } else if (contract.status === 'pending_upload') {
                          navigate(`/contracts/${contract._id}?upload=1`);
                        } else {
                          navigate(`/contracts/${contract._id}`);
                        }
                      }}
                    >
                      <TableCell sx={{ fontSize: '14px', color: '#111827', py: 2, borderBottom: '1px solid #f3f4f6' }}>
                        {vendorNumber}
                      </TableCell>
                      <TableCell sx={{ fontSize: '14px', color: '#111827', py: 2, borderBottom: '1px solid #f3f4f6' }}>
                        {supplierName}
                      </TableCell>
                      <TableCell sx={{ fontSize: '14px', color: '#111827', py: 2, borderBottom: '1px solid #f3f4f6' }}>
                        Registered Supplier
                      </TableCell>
                      <TableCell sx={{ fontSize: '14px', color: '#111827', py: 2, borderBottom: '1px solid #f3f4f6' }}>
                        {formatDate(getApplicationUpdatedAt(contract))}
                      </TableCell>
                      <TableCell sx={{ py: 2, borderBottom: '1px solid #f3f4f6' }}>
                        {getContractStatusChip(contract)}
                      </TableCell>
                      <TableCell sx={{ py: 2, borderBottom: '1px solid #f3f4f6' }}>
                        <LatestComment comment={contract.latestComment} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, borderBottom: '1px solid #f3f4f6' }}>
                        {canOpen && (
                          <IconButton
                            size="small"
                            sx={{
                              color: '#578A18',
                              animation: `${bounceRight} 1.2s ease-in-out infinite`,
                              '&:hover': {
                                color: '#467014',
                                backgroundColor: 'transparent',
                                animationDuration: '0.5s',
                              },
                            }}
                          >
                            <DottedArrowIcon size={18} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Table Footer / Pagination */}
          {!loading && contracts.length > 0 && (
            <DataTablePagination
              page={page}
              total={pagination.total || 0}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              loading={loading}
            />
          )}
        </TableContainer>
      </Container>
      <Footer />
    </Box>
  );
};

export default ContractList;
